// People on screen: the avatar (initials on the person's own hue), the chip a
// task or date wears, the picker that links something to someone, and the
// sheet that adds or edits a person.
import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Check, Plus, Search, Trash2 } from 'lucide-react'
import { usePeople } from '@/db/hooks'
import { CIRCLES, keyOf, type Circle, type PersonSpec } from '@/kinds/person/schema'
import type { PersonResource } from '@/kinds/person/apply'
import { firstOf, initials } from '@/lib/people'
import { personHue, tintText, tokens } from '@/lib/colour'
import { Icon } from '@/lib/icons'
import { mutate } from '@/runtime'
import { Button, Field, Sheet, cx } from './ui'

export const CIRCLE_LABEL: Record<Circle, string> = {
  partner: 'Partner',
  family: 'Family',
  friend: 'Friends',
  work: 'Work',
  other: 'Others',
}
/** The one-person word for a circle, for the person page. */
const CIRCLE_ONE: Record<Circle, string> = {
  partner: 'Partner',
  family: 'Family',
  friend: 'Friend',
  work: 'Work',
  other: 'Other',
}
export const circleOne = (c?: Circle) => (c ? CIRCLE_ONE[c] : '')

/** Initials, or their icon, on a disc of their hue. */
export const Avatar = ({ person, size = 36 }: { person: PersonResource; size?: number }) => {
  const hue = personHue(person.spec.key)
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-full font-semibold leading-none select-none"
      style={{
        width: size,
        height: size,
        background: tokens(hue).soft,
        color: tintText(hue),
        fontSize: size * 0.38,
      }}
      aria-hidden
    >
      {person.spec.icon ? <Icon value={person.spec.icon} size={size * 0.5} /> : initials(person.spec.name)}
    </span>
  )
}

/** A small chip with a person's first name, opening their page. */
export const PersonChip = ({ person }: { person: PersonResource }) => {
  const hue = personHue(person.spec.key)
  return (
    <Link
      to="/people/$id"
      params={{ id: person.id }}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 rounded-full pl-0.5 pr-2 h-5 text-[11px] leading-none"
      style={{ background: tokens(hue).soft, color: tintText(hue) }}
      data-testid="person-chip"
    >
      <Avatar person={person} size={16} />
      {firstOf(person)}
    </Link>
  )
}

/**
 * Choose people for something: tick who it is with. `single` for a date (one
 * person). Typing a name nobody has offers to add them.
 */
export const PeoplePicker = ({
  chosen,
  onChange,
  onClose,
  single,
  title = 'Who',
}: {
  chosen: string[]
  onChange: (ids: string[]) => void
  onClose: () => void
  single?: boolean
  title?: string
}) => {
  const people = usePeople()
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const shown = needle
    ? people.filter(
        (p) =>
          p.spec.name.toLowerCase().includes(needle) ||
          p.spec.key.includes(keyOf(needle)) ||
          (p.spec.aka ?? []).some((a) => a.toLowerCase().includes(needle)),
      )
    : people
  // the owner first, the way "You and Keira" reads
  const ordered = [...shown].sort((a, b) => Number(!!b.spec.me) - Number(!!a.spec.me))
  const toggle = (id: string) => {
    if (single) {
      onChange(chosen.includes(id) ? [] : [id])
      onClose()
      return
    }
    onChange(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id])
  }
  const addNew = async () => {
    const name = q.trim()
    if (!name) return
    const r = await mutate.create('Person', { name, key: uniqueKey(keyOf(name) || 'someone', people) })
    setQ('')
    toggle(r.id)
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={title}
      footer={
        single ? undefined : (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        )
      }
    >
      <label className="flex items-center gap-2 rounded-full bg-raised border border-line px-3 min-h-10 mb-3">
        <Search size={15} className="text-faint shrink-0" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !shown.length) void addNew()
          }}
          placeholder="Find or add someone"
          className="bare-input flex-1 min-w-0"
          data-testid="people-picker-search"
        />
      </label>
      <ul className="group-list" data-testid="people-picker">
        {ordered.map((p) => {
          const on = chosen.includes(p.id)
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => toggle(p.id)}
                className="w-full flex items-center gap-3 px-3.5 min-h-12 py-1.5 text-left"
                aria-pressed={on}
              >
                <Avatar person={p} size={30} />
                <span className="flex-1 min-w-0 truncate text-[15px]">
                  {p.spec.me ? (
                    <>
                      You <span className="text-faint">{p.spec.name}</span>
                    </>
                  ) : (
                    p.spec.name
                  )}
                </span>
                {on && <Check size={17} className="text-accent-text shrink-0" aria-hidden />}
              </button>
            </li>
          )
        })}
        {needle && !people.some((p) => p.spec.name.toLowerCase() === needle) && (
          <li>
            <button
              type="button"
              onClick={() => void addNew()}
              className="w-full flex items-center gap-3 px-3.5 min-h-12 py-1.5 text-left text-accent-text"
              data-testid="people-picker-add"
            >
              <span className="h-[30px] w-[30px] rounded-full border border-dashed border-line-strong inline-flex items-center justify-center">
                <Plus size={15} aria-hidden />
              </span>
              Add “{q.trim()}”
            </button>
          </li>
        )}
        {!needle && !people.length && (
          <li className="px-3.5 py-3 text-sm text-faint">Nobody yet. Type a name to add someone.</li>
        )}
      </ul>
    </Sheet>
  )
}

/** A handle nobody else has: sam, sam-2, sam-3… */
export const uniqueKey = (key: string, people: PersonResource[], self?: string): string => {
  const taken = new Set(people.filter((p) => p.id !== self).map((p) => p.spec.key))
  if (!taken.has(key)) return key
  for (let i = 2; ; i++) if (!taken.has(`${key.slice(0, 29)}-${i}`)) return `${key.slice(0, 29)}-${i}`
}

/** Add or edit a person. The handle follows the name until it is edited by hand. */
export const PersonSheet = ({
  resource,
  onDone,
}: {
  resource?: PersonResource
  onDone: (id?: string) => void
}) => {
  const people = usePeople()
  const navigate = useNavigate()
  const [name, setName] = useState(resource?.spec.name ?? '')
  const [key, setKey] = useState(resource?.spec.key ?? '')
  const [keyTouched, setKeyTouched] = useState(!!resource)
  const [aka, setAka] = useState((resource?.spec.aka ?? []).join(', '))
  const [circle, setCircle] = useState<Circle | undefined>(resource?.spec.circle)
  const [me, setMe] = useState(!!resource?.spec.me)
  // only one person is you: the switch is offered while nobody else is
  const someoneElseIsMe = people.some((p) => p.id !== resource?.id && p.spec.me)
  const [error, setError] = useState<string | null>(null)
  const handle = keyTouched ? key : uniqueKey(keyOf(name) || '', people, resource?.id)
  const clash = people.some((p) => p.id !== resource?.id && p.spec.key === handle)

  const save = async () => {
    setError(null)
    if (!name.trim()) return setError('Give them a name')
    if (!/^[a-z0-9_-]{1,32}$/.test(handle))
      return setError('The handle is lowercase letters, digits, - and _')
    if (clash) return setError('Someone else has that handle')
    const spec: Partial<PersonSpec> = {
      name: name.trim(),
      key: handle,
      aka: aka
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
        .slice(0, 10),
      circle,
      me,
    }
    try {
      if (resource) {
        const patch: Record<string, unknown> = {}
        if (spec.name !== resource.spec.name) patch.name = spec.name
        if (spec.key !== resource.spec.key) patch.key = spec.key
        if (JSON.stringify(spec.aka) !== JSON.stringify(resource.spec.aka ?? []))
          patch.aka = spec.aka?.length ? spec.aka : null
        if (spec.circle !== resource.spec.circle) patch.circle = spec.circle ?? null
        if (!!resource.spec.me !== me) patch.me = me || null
        if (Object.keys(patch).length) await mutate.patch(resource.id, { spec: patch })
        onDone(resource.id)
      } else {
        const r = await mutate.create('Person', {
          ...spec,
          aka: spec.aka?.length ? spec.aka : undefined,
          circle: spec.circle,
          me: me || undefined,
        })
        onDone(r.id)
      }
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <Sheet
      open
      onClose={() => onDone()}
      title={resource ? 'Edit' : 'Add someone'}
      footer={
        <>
          {resource && (
            <Button
              variant="quiet"
              className="mr-auto"
              onClick={() =>
                void mutate.remove(resource.id).then(() => {
                  onDone()
                  void navigate({ to: '/people' })
                })
              }
              data-testid="person-delete"
            >
              <Trash2 size={15} /> Delete
            </Button>
          )}
          <Button onClick={() => onDone()}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} data-testid="person-save">
            Save
          </Button>
        </>
      }
    >
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Keira"
          className="w-full"
          autoFocus={!resource}
          data-testid="person-name"
        />
      </Field>
      <Field
        label="Handle"
        hint="What their captures and share link carry, and what @ finds them by in quick add."
        error={clash ? 'Someone else has that handle' : undefined}
      >
        <input
          value={handle}
          onChange={(e) => {
            setKeyTouched(true)
            setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))
          }}
          className="w-full"
          maxLength={32}
          data-testid="person-key"
        />
      </Field>
      <Field label="Also known as" hint="Other names, commas between: Mum, Mother">
        <input
          value={aka}
          onChange={(e) => setAka(e.target.value)}
          className="w-full"
          data-testid="person-aka"
        />
      </Field>
      <div className="mb-3">
        <span className="block text-[13px] font-semibold text-muted mb-1.5">Circle</span>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Circle">
          {CIRCLES.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={circle === c}
              onClick={() => setCircle(circle === c ? undefined : c)}
              className={cx(
                'rounded-full min-h-9 px-3 text-[13px] border transition-colors',
                circle === c
                  ? 'bg-accent text-on-accent border-transparent'
                  : 'border-line text-muted hover:text-fg',
              )}
              data-testid={`person-circle-${c}`}
            >
              {CIRCLE_ONE[c]}
            </button>
          ))}
        </div>
      </div>
      {!someoneElseIsMe && (
        <label className="flex items-center gap-3 min-h-11 mb-3 text-[15px]">
          <input
            type="checkbox"
            checked={me}
            onChange={(e) => setMe(e.target.checked)}
            data-testid="person-me"
          />
          <span>
            This is me
            <span className="block text-xs text-faint">
              So dates and tasks can include you: “You and Keira”.
            </span>
          </span>
        </label>
      )}
      {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
    </Sheet>
  )
}
